import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Users, Inbox, MessageSquare, TrendingUp, ArrowRight } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";
import MyWeek from "./MyWeek";
import NoticeboardTeaser from "./NoticeboardTeaser";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ clients: 0, active: 0, referrals: 0, messages: 0 });
  const [recent, setRecent] = useState([]);

  const loadDashboard = useCallback(async () => {
    try {
      const [clientsRes, referralsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/referrals`),
        axios.get(`${API}/contact`),
      ]);
      const clients = clientsRes.data || [];
      const referrals = referralsRes.data || [];
      const messages = messagesRes.data || [];
      setStats({
        clients: clients.length,
        active: clients.filter((c) => c.status === "active").length,
        referrals: referrals.filter((r) => r.status === "new").length,
        messages: messages.filter((m) => m.status === "new").length,
      });
      setRecent(clients.slice(0, 5));
    } catch (e) {
      console.error("Failed to load dashboard:", e);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  return (
    <div data-testid="staff-dashboard">
      <div className="mb-10">
        <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">Welcome back</div>
        <h1 className="font-display text-4xl lg:text-5xl text-[#1F2A2E]">
          Hi {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-[#4A595E] mt-3 text-lg">Here's what's happening in your caseload today.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-testid="dashboard-stats">
        <Stat label="Your clients" value={stats.clients} icon={Users} link="/staff/clients" color="coral" testId="stat-clients" />
        <Stat label="Active cases" value={stats.active} icon={TrendingUp} color="orange" testId="stat-active" />
        <Stat label="New referrals" value={stats.referrals} icon={Inbox} link="/staff/referrals" color="navy" testId="stat-referrals" />
        <Stat label="Unread messages" value={stats.messages} icon={MessageSquare} link="/staff/messages" color="navy" testId="stat-messages" />
      </div>

      {/* My Week: personal + (admin) team pulse */}
      <div className="mb-8">
        <MyWeek />
      </div>

      {/* Team noticeboard teaser */}
      <div className="mb-12">
        <NoticeboardTeaser />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl p-7 border border-[#F0E5DE]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl text-[#1F2A2E]">Recently updated clients</h2>
            <Link to="/staff/clients" className="text-sm font-semibold text-[#E2718A] flex items-center gap-1.5">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-center py-12 text-[#6B7A80]">
              <Users className="w-10 h-10 mx-auto mb-3 text-[#F4C3CC]" />
              <p className="text-sm">No clients yet. Add your first one below.</p>
              <Link to="/staff/clients" className="btn-primary mt-5 text-sm">Add a client</Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#F0E5DE]">
              {recent.map((c) => (
                <li key={c.id}>
                  <Link to={`/staff/clients/${c.id}`} className="flex items-center justify-between py-3 hover:bg-[#F1E4D5] -mx-3 px-3 rounded-lg transition" data-testid={`recent-client-${c.id}`}>
                    <div>
                      <div className="font-medium text-[#1F2A2E]">{c.first_name} {c.last_name}</div>
                      <div className="text-xs text-[#6B7A80] mt-0.5">
                        {c.ndis_number || "No NDIS #"} · {c.assigned_to_name || "Unassigned"}
                      </div>
                    </div>
                    <StatusBadge status={c.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[#1A363D] text-white rounded-3xl p-7">
          <h3 className="font-display text-xl mb-3">Quick links</h3>
          <p className="text-sm text-white/70 mb-6">Jump straight into common tasks.</p>
          <div className="space-y-2">
            <QuickLink to="/staff/clients" label="Add new client" />
            <QuickLink to="/staff/referrals" label="Review referrals" />
            <QuickLink to="/staff/messages" label="Read messages" />
            {user?.role === "admin" && <QuickLink to="/staff/team" label="Manage team" />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, link, color, testId }) {
  const tones = {
    coral: "bg-[#FCE7EB] text-[#E2718A]",
    orange: "bg-[#FCEADB] text-[#EA823A]",
    navy: "bg-[#DDE7E9] text-[#284952]",
  };
  const inner = (
    <div className="bg-white rounded-3xl p-6 border border-[#F0E5DE] hover:border-[#F4C3CC] transition" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${tones[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-display text-3xl text-[#1F2A2E] mb-1">{value}</div>
      <div className="text-sm text-[#6B7A80]">{label}</div>
    </div>
  );
  return link ? <Link to={link} className="block">{inner}</Link> : inner;
}

function QuickLink({ to, label }) {
  return (
    <Link to={to} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm transition">
      {label} <ArrowRight className="w-4 h-4 text-[#EA823A]" />
    </Link>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: { bg: "#DCEFE0", text: "#1F6B3A", label: "Active" },
    inactive: { bg: "#E8E8E8", text: "#555", label: "Inactive" },
    on_hold: { bg: "#FCEADB", text: "#A0521A", label: "On hold" },
    new: { bg: "#FCE7EB", text: "#A12545", label: "New" },
    converted: { bg: "#DCEFE0", text: "#1F6B3A", label: "Converted" },
  };
  const s = map[status] || { bg: "#F0E5DE", text: "#555", label: status };
  return (
    <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
}
